/**
 * Contains the different sort data-types
 * @namespace
 */
export const SORT_DATA_TYPES = Object.freeze({
  /**
   * DataType is a number
   * @member {String}
   * @memberof SORT_DATA_TYPES#
   */
  NUMBER: 'number',

  /**
   * DataType is a string
   * @member {String}
   * @memberof SORT_DATA_TYPES#
   */
  STRING: 'string',

  /**
   * DataType is a date
   * @member {String}
   * @memberof SORT_DATA_TYPES#
   */
  DATE: 'date',

  /**
   * DataType is a date string, for example '1988-03-14'
   * @member {String}
   * @memberof SORT_DATA_TYPES#
   */
  DATE_STRING: 'date-string',

  /**
   * DataType is a boolean
   * @member {String}
   * @memberof SORT_DATA_TYPES#
   */
  BOOL: 'boolean'
});

/** @module array */

/**
 * Transforms an array to object. The give key becomes the new property in the object.
 * @param {Object[]} array  - Array to transform
 * @param {string} [keyField] - Key to use as new property, default key = 'id'
 * @param {string} [valueField] - Uses value from field on the given keyField, default
 * @returns {Object} object
 * @func
 */
export const arrayToObject = (array, keyField = 'id', valueField) =>
  array.reduce((obj, item) => {
    if (valueField) {
      obj[item[keyField]] = item[valueField];
    } else {
      obj[item[keyField]] = item;
    }
    return obj;
  }, {});

/**
 * Removes given item from array
 * @param {Object[]} array
 * @param {Object} item
 * @func
 */
export const removeItemFromArray = (array, item) => {
  const index = array.indexOf(item);
  if (index !== -1) {
    array.splice(index, 1);
  }
  return array;
};

const _getValueToSort = (value, dataType) => {
  let newValue = value;
  if (dataType === SORT_DATA_TYPES.STRING) newValue = value.toLowerCase();
  if (dataType === SORT_DATA_TYPES.DATE) newValue = value.getTime();
  if (dataType === SORT_DATA_TYPES.DATE_STRING) {
    newValue = new Date(value).getTime();
  }
  return newValue;
};
const _defaultSortKeys = [
  {
    key: 'name',
    desc: false,
    dataType: SORT_DATA_TYPES.STRING
  }
];
const _defaultSortOptions = { reference: false, deepCopy: false };

/**
 * Sorts an array of objects and returns it, default order direction is ASC on key 'name'
 * @param {Object[]} array - Array to sort
 * @param {Object} [paramOptions] - Optional options for sorting, default sorting on key 'name' and asc
 * @param {Object[]} [paramOptions.keys] - Defines sorting on which keys, in the keys array order
 * @param {string} paramOptions.keys[].key - Defines the sorting key
 * @param {boolean} [paramOptions.keys[].desc] - Defines the sorting direction, desc or asc, default false
 * @param {string} [paramOptions.keys[].dataType] - Defines the data type of the value (use SORT_DATA_TYPES), default string
 * @param {Object} [paramOptions.options] - Defines the reference or copy options
 * @param {boolean} [paramOptions.options.reference] - Returns same sorted array when true, otherwise its no reference array, default false
 * @param {boolean} [paramOptions.options.deepCopy] - Returns a deepCopy of array and objects when true, default false
 * @returns {Object[]} sortedArray
 * @func
 */
export const sort = (
  array,
  { keys = _defaultSortKeys, options = _defaultSortOptions } = {
    keys: _defaultSortKeys,
    options: _defaultSortOptions
  }
) => {
  const arrayToSort = options.deepCopy
    ? JSON.parse(JSON.stringify(array))
    : options.reference ? array : [...array];
  return arrayToSort.sort((a, b) => {
    let orderedValue = null;
    keys.some(o => {
      const type = o.dataType || SORT_DATA_TYPES.STRING;
      const desc = o.desc ? true : false;
      const theKey = o.key || 'name';

      const valueA = _getValueToSort(a[theKey], type);
      const valueB = _getValueToSort(b[theKey], type);

      if (!desc) {
        if (valueA > valueB) {
          orderedValue = 1;
          return true;
        }
        if (valueA < valueB) {
          orderedValue = -1;
          return true;
        }
      } else {
        if (valueA < valueB) {
          orderedValue = 1;
          return true;
        }
        if (valueA > valueB) {
          orderedValue = -1;
          return true;
        }
      }
      return false;
    });

    return orderedValue !== null ? orderedValue : 0;
  });
};



// WEBPACK FOOTER //
// ./src/utils/array.js